<template>
  <div class="flex w-full">
    <div class="relative flex flex-auto flex-grow-0 max-w-full mx-auto">
      <div ref="wrapper" class="tab-list__wrapper">
        <div
          ref="content"
          class="tab-list__content"
          @touchstart.passive="overflowCheck($event, onTouchStart)"
          @touchmove.passive="overflowCheck($event, onTouchMove)"
          @touchend.passive="overflowCheck($event, onTouchEnd)"
        >
          <div
            v-for="(item, i) in items"
            :key="item"
            v-text="item.meta.tabTitle ?? item.name"
            @click="handleClick($event.target, item)"
            class="text-white hover-hover:hover:bg-accent hover-hover:hover:text-brand rounded-3xl flex items-center justify-center w-40 h-12 px-6 text-center whitespace-normal cursor-pointer"
            :class="{ 'bg-white text-brand': selectedIndex === i }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
export default {
  props: {
    action: {
      type: Function
    },
    activeClass: {
      type: String,
      default: "v-slide-item--active"
    },
    centerActive: Boolean,
    nextIcon: {
      type: String,
      default: "$next"
    },
    prevIcon: {
      type: String,
      default: "$prev"
    },
    showArrows: {
      type: [Boolean, String],
      validator: v =>
        typeof v === "boolean" || ["always", "desktop", "mobile"].includes(v)
    },
    items: {
      type: Array
    }
  },
  data: () => ({
    selectedItem: { $el: undefined, value: {} },
    internalItemsLength: 0,
    isOverflowing: false,
    resizeTimeout: 0,
    startX: 0,
    scrollOffset: 0,
    widths: {
      content: 0,
      wrapper: 0
    }
  }),
  computed: {
    routeName() {
      return this.$route.name;
    },
    selectedIndex() {
      return (
        this.selectedItem &&
        _.findIndex(this.items, i => i.name === this.selectedItem.value.name)
      );
    },
    hasAffixes() {
      switch (this.showArrows) {
        // Always show arrows on desktop & mobile
        case "always":
          return true;

        // Always show arrows on desktop
        case "desktop":
          return this.isDesktop;

        // Show arrows on mobile when overflowing.
        // This matches the default 2.2 behavior
        case true:
          return this.isOverflowing || Math.abs(this.scrollOffset) > 0;

        // Always show on mobile
        case "mobile":
          return (
            !this.isDesktop ||
            this.isOverflowing ||
            Math.abs(this.scrollOffset) > 0
          );

        // https://material.io/components/tabs#scrollable-tabs
        // Always show arrows when
        // overflowed on desktop
        default:
          return (
            this.isDesktop &&
            (this.isOverflowing || Math.abs(this.scrollOffset) > 0)
          );
      }
    },
    hasNext() {
      if (!this.hasAffixes) return false;

      const { content, wrapper } = this.widths;

      // Check one scroll ahead to know the width of right-most item
      return content > Math.abs(this.scrollOffset) + wrapper;
    },
    hasPrev() {
      return this.hasAffixes && this.scrollOffset !== 0;
    }
  },
  watch: {
    isDesktop: "setWidths",
    internalValue: "setWidths", // inutile?
    isOverflowing: "setWidths",
    scrollOffset(val) {
      this.$refs.content.style.transform = `translateX(${-val}px)`;
    }
    // routeName: function(value) {
    //   this.setFromRoute(value);
    // }
  },
  mounted() {
    this.setFromRoute(this.routeName);
  },
  beforeUpdate() {
    this.internalItemsLength = (this.items || []).length;
  },

  updated() {
    if (this.internalItemsLength === (this.items || []).length) return;
    this.setWidths();
  },
  methods: {
    setFromRoute(routeName) {
      const item = _.find(this.items, i => i.name === routeName);
      const index = _.findIndex(this.items, item);
      this.handleClick(this.$refs.content.children[index], item);
      this.setWidths();
    },
    handleClick($el, item) {
      this.action(item);
      this.selectedItem = { $el: $el, value: item };
      this.setWidths();
    },
    calculateNewOffset(direction, widths, currentScrollOffset) {
      const newAbosluteOffset =
        currentScrollOffset + (direction === "prev" ? -1 : 1) * widths.wrapper;

      return Math.max(
        Math.min(newAbosluteOffset, widths.content - widths.wrapper),
        0
      );
    },
    onAffixClick(location) {
      this.$emit(`click:${location}`);
      this.scrollTo(location);
    },
    onResize() {
      /* istanbul ignore next */
      if (this._isDestroyed) return;

      this.setWidths();
    },
    onTouchStart(e) {
      const touch = e.changedTouches[0];
      const touchstartX = touch.clientX;

      const { content } = this.$refs;

      this.startX = this.scrollOffset + touchstartX;

      content.style.setProperty("transition", "none");
      content.style.setProperty("willChange", "transform");
    },
    onTouchMove(e) {
      const touch = e.changedTouches[0];
      const touchmoveX = touch.clientX;
      this.scrollOffset = this.startX - touchmoveX;
    },
    onTouchEnd() {
      const { content, wrapper } = this.$refs;
      const maxScrollOffset = content.clientWidth - wrapper.clientWidth;

      content.style.setProperty("transition", null);
      content.style.setProperty("willChange", null);

      /* istanbul ignore else */
      if (this.scrollOffset < 0 || !this.isOverflowing) {
        this.scrollOffset = 0;
      } else if (this.scrollOffset >= maxScrollOffset) {
        this.scrollOffset = maxScrollOffset;
      }
    },
    overflowCheck(e, fn) {
      //   e.stopPropagation();
      this.isOverflowing && fn(e);
    },
    scrollIntoView /* istanbul ignore next */() {
      if (!this.selectedItem && this.items.length) {
        const lastItemPosition = this.items[
          this.items.length - 1
        ].$el.getBoundingClientRect();
        const wrapperPosition = this.$refs.wrapper.getBoundingClientRect();

        if (wrapperPosition.left > lastItemPosition.left) {
          this.scrollTo("prev");
        }
      }

      if (!this.selectedItem) {
        return;
      }

      if (
        this.selectedIndex === 0 ||
        (!this.centerActive && !this.isOverflowing)
      ) {
        this.scrollOffset = 0;
      } else if (this.centerActive) {
        this.scrollOffset = this.calculateCenteredOffset(
          this.selectedItem.$el,
          this.widths
        );
      } else if (this.isOverflowing) {
        this.scrollOffset = this.calculateUpdatedOffset(
          this.selectedItem.$el,
          this.widths,
          this.scrollOffset
        );
      }
    },
    calculateUpdatedOffset(selectedElement, widths, currentScrollOffset) {
      const clientWidth = selectedElement.clientWidth;
      const offsetLeft = selectedElement.offsetLeft;

      const totalWidth = widths.wrapper + currentScrollOffset;
      const itemOffset = clientWidth + offsetLeft;
      const additionalOffset = clientWidth * 0.4;

      if (offsetLeft <= currentScrollOffset) {
        currentScrollOffset = Math.max(offsetLeft - additionalOffset, 0);
      } else if (totalWidth <= itemOffset) {
        currentScrollOffset = Math.min(
          currentScrollOffset - (totalWidth - itemOffset - additionalOffset),
          widths.content - widths.wrapper
        );
      }

      return currentScrollOffset;
    },
    calculateCenteredOffset(selectedElement, widths) {
      const { offsetLeft, clientWidth } = selectedElement;

      const offsetCentered = offsetLeft + clientWidth / 2 - widths.wrapper / 2;
      return Math.min(
        widths.content - widths.wrapper,
        Math.max(0, offsetCentered)
      );
    },
    scrollTo /* istanbul ignore next */(location) {
      this.scrollOffset = this.calculateNewOffset(
        location,
        {
          // Force reflow
          content: this.$refs.content ? this.$refs.content.clientWidth : 0,
          wrapper: this.$refs.wrapper ? this.$refs.wrapper.clientWidth : 0
        },
        this.scrollOffset
      );
    },
    setWidths /* istanbul ignore next */() {
      window.requestAnimationFrame(() => {
        const { content, wrapper } = this.$refs;

        this.widths = {
          content: content ? content.clientWidth : 0,
          wrapper: wrapper ? wrapper.clientWidth : 0
        };

        this.isOverflowing = this.widths.wrapper < this.widths.content;

        this.scrollIntoView();
      });
    }
  }
};
</script>

<style scoped>
.router-link-active {
  @apply bg-white text-brand;
}

.tab-list__wrapper {
  @apply bg-brand rounded-3xl whitespace-nowrap flex flex-auto overflow-hidden;
}

.tab-list__content {
  @apply leading-tight whitespace-nowrap relative flex items-center justify-around content-center flex-auto flex-shrink-0;
  transition: transform 1s;
}
</style>
